import type { PopperProps } from "@mui/base";
import escapeRegExp from "lodash/escapeRegExp";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useRef } from "react";

import { notEmpty } from "shared/lib/utils";

import { MaxKeywordLength } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { useDefaultSearchInputSate } from "scmp-app/components/search/hooks";

import { useDebouncedAutoComplete } from "./hooks";
import {
  ClearButton,
  ClearCircle,
  ClearFade,
  CloseIcon,
  Container,
  EndAdornment,
  StyledInput as input,
  LeadingMagnifier,
  SearchButton,
  StyledAutocomplete,
  StyledBaseInput,
} from "./styles";

type Props = {
  className?: string;
  onChange?: (value: string) => void;
};

export const SearchInput: FunctionComponent<Props> = ({ className, onChange }) => {
  const router = useRouter();
  const { currentValue, refineSearch, searchRouteFunction } = useSearchContext();

  const [searchInput, setSearchInput] = useDefaultSearchInputSate(currentValue);
  const hasText = notEmpty(searchInput?.trim());
  const hasUnsearchedInput = hasText && searchInput !== currentValue;

  const inputReference = useRef<HTMLInputElement | null>(null);
  const captureInputElement = useCallback((element: HTMLElement | null) => {
    if (!notEmpty(element)) return;
    const input = element.querySelector("input");
    inputReference.current = input;
  }, []);

  const {
    fetchSuggestions,
    resetSuggestions,
    suggestionsState: { result: suggestions },
  } = useDebouncedAutoComplete();

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    setSearchInput(currentValue);
  }, [currentValue, setSearchInput, refineSearch]);

  useEffect(() => {
    async function fetchData() {
      await fetchSuggestions(searchInput);
    }
    void fetchData();
  }, [fetchSuggestions, searchInput]);

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    inputReference?.current?.blur();
  }, [currentValue]);

  const handleSearch = useCallback(
    (value: null | string) => {
      if (!notEmpty(value?.trim())) return;
      const searchPageRoute = searchRouteFunction?.({ q: value.trim() });
      if (!searchPageRoute) return;

      resetSuggestions();

      const { asPath, ...restSearchPageRoute } = searchPageRoute;
      void router.push(restSearchPageRoute, asPath, { shallow: true });
    },
    [resetSuggestions, router, searchRouteFunction],
  );

  const handleReset = useCallback(() => {
    setSearchInput("");
    resetSuggestions();
  }, [resetSuggestions, setSearchInput]);

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: unknown,
      { inputValue }: { inputValue: string },
    ) => {
      const { key, ...restProps } = props;
      if (typeof option !== "string") return null;
      const parts = option.split(new RegExp(`(${escapeRegExp(inputValue)})`, "ig"));
      return (
        <li key={key} {...restProps}>
          <div>
            {parts.map((part, index) =>
              part.toLowerCase() === inputValue.toLowerCase() ? (
                <strong key={index}>{part}</strong>
              ) : (
                <span key={index}>{part}</span>
              ),
            )}
          </div>
        </li>
      );
    },
    [],
  );

  return (
    <Container className={className}>
      <StyledAutocomplete
        autoFocus={false}
        disablePortal
        freeSolo
        onChange={(_event, value) => {
          const newValue = value as null | string;
          handleSearch(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          onChange?.(newInputValue);
          setSearchInput(newInputValue);
        }}
        onKeyDown={event => {
          if (event.key !== "Enter") return;
          handleSearch(searchInput);
        }}
        options={suggestions}
        ref={captureInputElement}
        renderInput={parameters => (
          <StyledBaseInput
            {...parameters.InputProps}
            endAdornment={
              hasText ? (
                <EndAdornment>
                  <ClearButton
                    aria-label="Clear search"
                    onClick={handleReset}
                    onMouseDown={event => event.stopPropagation()}
                    type="button"
                  >
                    <ClearFade />
                    <ClearCircle>
                      <CloseIcon />
                    </ClearCircle>
                  </ClearButton>
                  {hasUnsearchedInput ? (
                    <SearchButton
                      onClick={() => {
                        handleSearch(searchInput);
                      }}
                      onMouseDown={event => event.stopPropagation()}
                      type="button"
                    >
                      SEARCH
                    </SearchButton>
                  ) : null}
                </EndAdornment>
              ) : null
            }
            placeholder="Search articles or topics"
            slotProps={{
              input: {
                ...parameters.inputProps,
                maxLength: MaxKeywordLength,
              },
            }}
            slots={{ input }}
            startAdornment={<LeadingMagnifier />}
          />
        )}
        renderOption={renderOption}
        slotProps={{
          paper: {
            style: {
              border: suggestions.length > 0 ? "1px solid #e5e5e5" : "none",
              borderRadius: 0,
              boxShadow: "none",
              inlineSize: "100%",
              maxBlockSize: "70vh",
            },
          },
          popper: {
            popperOptions: {
              modifiers: [
                {
                  enabled: true,
                  fn: ({ state }) => {
                    state.elements.popper.style.inlineSize = "100%";
                    state.elements.popper.style.maxBlockSize = "70vh";
                  },
                  name: "customInlineSize",
                  phase: "afterWrite",
                },
              ] satisfies PopperProps["modifiers"],
              placement: "bottom",
            },
          },
        }}
        value={searchInput}
      />
    </Container>
  );
};
